import React from 'react';
import { formatDistance, calculateDistance } from '../../../services/location.service';

/**
 * TimeEntryGPSInfo - Display GPS information for a time entry
 * Shows arrival/departure locations and distances
 * 
 * @param {Object} props
 * @param {Object} props.timeEntry - Time entry with GPS fields
 * @param {Object} props.propertyLocation - Property coordinates {latitude, longitude} (optional)
 */
function TimeEntryGPSInfo({ timeEntry, propertyLocation }) {
  const hasArrivalGPS = timeEntry.arrivedAtLatitude && timeEntry.arrivedAtLongitude;
  const hasDepartureGPS = timeEntry.departedAtLatitude && timeEntry.departedAtLongitude;

  if (!hasArrivalGPS && !hasDepartureGPS) {
    return (
      <span className="text-xs text-gray-400 italic">No GPS data</span>
    );
  }

  const getArrivalDistance = () => {
    if (!hasArrivalGPS || !propertyLocation?.latitude || !propertyLocation?.longitude) {
      return null;
    }
    return calculateDistance(
      timeEntry.arrivedAtLatitude,
      timeEntry.arrivedAtLongitude,
      propertyLocation.latitude,
      propertyLocation.longitude
    );
  };

  const getDepartureDistance = () => {
    if (!hasDepartureGPS || !propertyLocation?.latitude || !propertyLocation?.longitude) {
      return null;
    }
    return calculateDistance(
      timeEntry.departedAtLatitude,
      timeEntry.departedAtLongitude,
      propertyLocation.latitude,
      propertyLocation.longitude
    );
  };

  const arrivalDistance = getArrivalDistance();
  const departureDistance = getDepartureDistance();

  return (
    <div className="mt-2 space-y-1 text-xs">
      {hasArrivalGPS && (
        <div className="flex items-center text-gray-600">
          <svg className="h-3 w-3 mr-1 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="font-medium">Arrived:</span>
          <span className="ml-1">
            {timeEntry.arrivedAtLatitude.toFixed(6)}, {timeEntry.arrivedAtLongitude.toFixed(6)}
          </span>
          {timeEntry.arrivedAtAccuracy && (
            <span className="ml-1 text-gray-400">(±{timeEntry.arrivedAtAccuracy}m)</span>
          )}
          {arrivalDistance !== null && (
            <span className={`ml-2 ${arrivalDistance > 100 ? 'text-orange-600' : 'text-green-600'}`}>
              {formatDistance(arrivalDistance)} from property
            </span>
          )}
        </div>
      )}

      {hasDepartureGPS && (
        <div className="flex items-center text-gray-600">
          <svg className="h-3 w-3 mr-1 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          <span className="font-medium">Departed:</span>
          <span className="ml-1">
            {timeEntry.departedAtLatitude.toFixed(6)}, {timeEntry.departedAtLongitude.toFixed(6)}
          </span>
          {timeEntry.departedAtAccuracy && (
            <span className="ml-1 text-gray-400">(±{timeEntry.departedAtAccuracy}m)</span>
          )}
          {departureDistance !== null && (
            <span className={`ml-2 ${departureDistance > 100 ? 'text-orange-600' : 'text-green-600'}`}>
              {formatDistance(departureDistance)} from property
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default TimeEntryGPSInfo;
